import { QUESTION_TYPE } from '../components/Activity/Question';
import type {
  CmsModuleDefinition,
  CmsModuleActivity,
} from '../schemas/content-module';
import type { CmsProjectQuestion } from '../schemas/applied-project';
import type {
  DashboardPayload,
  ActivityPayload,
  QuestionPayload,
} from '../hydra';

/**
 * Converts a CMS module definition into the DashboardPayload shape that
 * DashboardPage expects.
 */
export function cmsModuleToDashboardResponse(
  def: CmsModuleDefinition
): DashboardPayload {
  const missions: DashboardPayload['missions'] = [];

  for (const [weekIndex, week] of def.weeks.entries()) {
    for (const [missionIndex, mission] of week.missions.entries()) {
      missions.push({
        slug: mission.slug,
        title: mission.title,
        index: missionIndex,
        sentAt: null,
        content: {
          id: -1,
          slug: def.slug,
          title: def.title,
        },
        week: {
          id: -(weekIndex + 1),
          slug: week.slug,
          title: week.title,
          index: weekIndex,
        },
        activities: mission.activities.map((act) => ({
          slug: act.slug,
          isCore: act.isCore ?? true,
          title: act.title,
          instructorReview: null,
          instructorScore: null,
          isSubmitted: false,
          isCompleted: false,
          isSent: true,
        })),
      });
    }
  }

  return { missions, links: [] };
}

function cmsQuestionToPayload(
  q: CmsProjectQuestion,
  index: number
): QuestionPayload {
  const now = new Date().toISOString();

  return {
    id: -(index + 1),
    updated_at: now,
    created_at: now,
    type: q.type as QUESTION_TYPE,
    text: q.text,
    hints: q.hints ?? null,
    exercise_id: null,
    slug: q.slug,
    setup_sql: 'setupSQL' in q ? (q.setupSQL as string) : null,
    answer_dql:
      'answerDQL' in q ? (q.answerDQL as string | null) ?? null : null,
    verification_sql:
      'verificationSQL' in q
        ? (q.verificationSQL as string | null) ?? null
        : null,
    index_in_exercise: q.indexInExercise,
    looker_link:
      'lookerLink' in q ? (q.lookerLink as string | null) ?? null : null,
    tableau_link:
      'tableauLink' in q ? (q.tableauLink as string | null) ?? null : null,
    google_sheets_link:
      'googleSheetsLink' in q
        ? (q.googleSheetsLink as string | null) ?? null
        : null,
    model_answer: q.modelAnswer ?? null,
    notes: q.notes ?? null,
    powerbi_csv: 'powerbiCSV' in q ? (q.powerbiCSV as string) : null,
    initial_py:
      'initialPy' in q ? (q.initialPy as string | null) ?? null : null,
    output_regexes_py:
      'outputRegexesPy' in q
        ? (q.outputRegexesPy as string[] | null) ?? null
        : null,
    input_regexes_py:
      'inputRegexesPy' in q
        ? (q.inputRegexesPy as string[] | null) ?? null
        : null,
    expect_error_py:
      'expectErrorPy' in q ? (q.expectErrorPy as boolean | null) ?? null : null,
    intro_lesson_text: q.introLessonText,
    setup_py: 'setupPy' in q ? (q.setupPy as string | null) ?? null : null,
    llm_initial_prompt:
      'llmInitialPrompt' in q
        ? (q.llmInitialPrompt as string | null) ?? null
        : null,
    multiple_choice_answers:
      'multipleChoiceAnswers' in q && q.multipleChoiceAnswers
        ? {
            correct_answer: (
              q.multipleChoiceAnswers as {
                correctAnswer: string;
                distractors: string[];
              }
            ).correctAnswer,
            distractors: (
              q.multipleChoiceAnswers as {
                correctAnswer: string;
                distractors: string[];
              }
            ).distractors,
          }
        : null,
    strict_text_answers:
      'strictTextAnswers' in q ? (q.strictTextAnswers as string[]) : null,
  };
}

type FlatActivity = {
  activity: CmsModuleActivity;
  missionSlug: string;
  indexInMission: number;
};

function flattenActivities(def: CmsModuleDefinition): FlatActivity[] {
  const result: FlatActivity[] = [];
  for (const week of def.weeks) {
    for (const mission of week.missions) {
      for (let i = 0; i < mission.activities.length; i++) {
        result.push({
          activity: mission.activities[i],
          missionSlug: mission.slug,
          indexInMission: i,
        });
      }
    }
  }
  return result;
}

/**
 * Converts a single CMS module activity into a mock ActivityPayload suitable
 * for rendering in the learner-facing ActivityLayout.
 */
export function cmsModuleActivityToActivityResponse(
  def: CmsModuleDefinition,
  activitySlug: string
): ActivityPayload | null {
  const all = flattenActivities(def);
  const flatIndex = all.findIndex((f) => f.activity.slug === activitySlug);
  if (flatIndex === -1) return null;

  const { activity: act, indexInMission } = all[flatIndex];
  const next = all[flatIndex + 1] ?? null;
  const now = new Date().toISOString();

  return {
    id: -(flatIndex + 1),
    updated_at: now,
    created_at: now,
    is_core: act.isCore ?? true,
    title: act.title,
    summary: act.summary ?? null,
    external_url: null,
    type: 'EXERCISE',
    mission_id: null,
    estimate_seconds: act.estimateSeconds ?? 3600,
    index_in_mission: indexInMission,
    exercise_id: null,
    is_teams_only: false,
    slug: act.slug,
    activity_session_id: -1,
    activity_session_instructor_review: null,
    activity_session_instructor_score: null,
    activity_session_instructor_question_comments: [],
    latest_submission: null,
    exercise: {
      id: -(flatIndex + 1),
      updated_at: now,
      created_at: now,
      description: act.description,
      lesson: act.lesson,
      difficulty: act.difficulty ?? 2,
      activity_id: -(flatIndex + 1),
      slug: act.slug,
      questions: act.questions.map((q, i) => cmsQuestionToPayload(q, i)),
    },
    content_id: -1,
    content_slug: def.slug,
    content_title: def.title,
    next_activity_slug: next?.activity.slug ?? null,
    next_mission_slug: next?.missionSlug ?? null,
  };
}
